import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import { rootReducer } from "./rootReducer";
import { listenerMiddleware } from "./syncMiddleware";
import { preferencesInitialState } from "./slices/preferencesSlice";
import type { PersistStorage } from "../platform";

// The default reconciler (autoMergeLevel1) replaces each persisted slice wholesale
// and does NOT backfill fields added to a slice's initialState later — so a device
// that persisted `preferences` before a field existed rehydrates without it. These
// migrations repair that. Bump `version` and add a numbered entry when adding fields.
// Persisted state carries arbitrary historical shapes across app versions, so these
// migrations are intentionally loosely typed. One disable covers the whole manifest.
/* eslint-disable @typescript-eslint/no-explicit-any */
const migrations = {
  // v2: backfill preference fields missing from older persisted state (themeMode
  // was added after launch). Existing user choices win; only absent keys are filled.
  2: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v3: same backfill for weekStartsOn (added with the meal planner).
  3: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v4: same backfill for the persisted sort prefs (recipeSort/collectionSort).
  4: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v5: same backfill for the showRating meta-visibility toggle.
  // NOTE: if the monetization branch also adds a v5, renumber one at merge.
  5: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v6: same backfill for the unitSystem display preference.
  6: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v7: same backfill for activeShoppingListId (added with multiple shopping lists).
  // The new `shoppingLists` slice needs no migration — autoMergeLevel1 keeps its
  // reducer initialState when the key is absent from persisted state.
  7: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v8: same backfill for the `locale` preference (added with i18n).
  8: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v9: default analyticsConsent to false (opt-out) for existing installs, so
  // analytics stays off until the user explicitly opts in.
  9: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v10: same backfill for the review-prompt bookkeeping fields
  // (lastReviewPromptVersion / lastReviewPromptAt / reviewPromptCount).
  10: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v11: same backfill for `homeCards` (home-screen card visibility, all default on).
  11: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v12: same backfill for `homeCardOrder` (home-screen card order, default order).
  12: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      preferences: { ...preferencesInitialState, ...state.preferences },
    };
  },
  // v13: "Recently added" replaces "Recently viewed" as the default home card.
  // Add the new card (on), default Recently viewed off, and slot the new card into
  // the order where Recently viewed sits. Other card choices are preserved.
  13: (state: any) => {
    if (!state) return state;
    const prefs = { ...preferencesInitialState, ...state.preferences };
    const prevCards = (state.preferences && state.preferences.homeCards) || {};
    prefs.homeCards = {
      ...preferencesInitialState.homeCards,
      ...prevCards,
      recentlyAdded: true,
      recentlyViewed: false,
    };
    const order = Array.isArray(state.preferences && state.preferences.homeCardOrder)
      ? [...state.preferences.homeCardOrder]
      : [...preferencesInitialState.homeCardOrder];
    if (!order.includes("recentlyAdded")) {
      const i = order.indexOf("recentlyViewed");
      if (i >= 0) order.splice(i, 0, "recentlyAdded");
      else order.push("recentlyAdded");
    }
    prefs.homeCardOrder = order;
    return { ...state, preferences: prefs };
  },
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface MakeStoreOptions {
  // Storage engine for redux-persist, injected per platform (AsyncStorage on RN,
  // IndexedDB/localforage or createMemoryStorage() on web).
  storage: PersistStorage;
  // redux-persist root key (defaults to "root" to match the RN app's history).
  persistKey?: string;
}

// Assembles the shared store with the sync dirty-tracking middleware and
// redux-persist. Each app calls this once with its own storage adapter.
export function makeStore({ storage, persistKey = "root" }: MakeStoreOptions) {
  const persistedReducer = persistReducer(
    {
      key: persistKey,
      version: 13,
      storage,
      blacklist: [],
      migrate: createMigrate(migrations),
    },
    rootReducer,
  );

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefault) =>
      getDefault({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).prepend(listenerMiddleware.middleware),
  });

  const persistor = persistStore(store);

  return { store, persistor };
}

export type AppStore = ReturnType<typeof makeStore>["store"];
export type AppDispatch = AppStore["dispatch"];
